import { useEffect, useState } from 'react';

/**
 * Subscribe to a CSS media query. Returns true if the query matches
 * the current viewport.
 *
 * SSR-safe: returns false on server render (no `window`), updates to
 * the real value on client mount. No FOUC for client-only apps (Vite
 * SPA); watch for hydration warnings if this is ever used under SSR.
 *
 * Usage:
 *   const isDesktop = useMediaQuery('(min-width: 1024px)');
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    // Initial sync (in case query changed between SSR and mount).
    setMatches(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}
